// Konto i sesje - trasy `worker api` (worker-go/api/api.go, PROJEKT.md 7).
import type {
  Account, AccountDeleteIn, AccountReply, ConsentOut, ConsentsReply, DeviceNameIn, GoogleIDReply, OKReply, SessionOut, SessionsReply, TankIn,
  TanksReply,
} from "../gen/kontrakt";
import { ustawZeton } from "./adres";
import { zapytanie } from "./zaplecze";

/** Konto z odpowiedzi logowania; żeton sesji (tylko aplikacja ze sklepu) do Keychain / Keystore. */
async function zalogowano(o: AccountReply): Promise<Account> {
  await ustawZeton(o.zeton);
  return o.konto;
}

export async function zaloguj(login: string, haslo: string, zapamietaj: boolean): Promise<Account> {
  return zalogowano(await zapytanie<AccountReply>("POST", "/api/logowanie", { login, haslo, zapamietaj }));
}

export async function zalogujGoogle(credential: string, zapamietaj: boolean): Promise<Account> {
  return zalogowano(await zapytanie<AccountReply>("POST", "/api/logowanie/google", { credential, zapamietaj }));
}

export async function googleId(): Promise<string> {
  return (await zapytanie<GoogleIDReply>("GET", "/api/logowanie/google")).client_id;
}

export async function wyslijAktywacje(login: string): Promise<void> {
  await zapytanie<OKReply>("POST", "/api/logowanie/aktywacja", { login });
}

/** „Nie pamiętasz hasła?” - link na adres konta; odpowiedź zawsze ta sama (bez zdradzania, czy konto istnieje). */
export async function przypomnijHaslo(login: string): Promise<void> {
  await zapytanie<OKReply>("POST", "/api/logowanie/reset", { login });
}

/** Nowe hasło z linku resetu (`?reset=<kod>`); wszystkie sesje konta kończą się. */
export async function noweHaslo(kod: string, nowe: string): Promise<void> {
  await zapytanie<OKReply>("POST", "/api/logowanie/nowe-haslo", { kod, nowe });
}

export async function wyloguj(): Promise<void> {
  try {
    await zapytanie<OKReply>("POST", "/api/wyloguj");
  } finally {
    await ustawZeton("");
  }
}

/** Bieżące konto (odnawia ciasteczko); błąd `brak_sesji` = niezalogowany. */
export async function konto(): Promise<Account> {
  return (await zapytanie<AccountReply>("GET", "/api/konto")).konto;
}

export async function zmienHaslo(stare: string, nowe: string): Promise<void> {
  await zapytanie<OKReply>("POST", "/api/konto/haslo", { stare, nowe });
}

/** Prośba o usunięcie konta (WYGLAD.md #57) - konto usuwa obsługa; `email` = adres konta wpisany na potwierdzenie. */
export async function prosbaOUsuniecie(email: string): Promise<void> {
  await zapytanie<OKReply>("POST", "/api/konto/usuniecie", { email } satisfies AccountDeleteIn);
}

export async function sesje(): Promise<SessionOut[]> {
  return (await zapytanie<SessionsReply>("GET", "/api/sesje")).sesje;
}

export async function zakonczSesje(id: string): Promise<void> {
  await zapytanie<OKReply>("DELETE", "/api/sesje/" + encodeURIComponent(id));
}

export async function wylogujWszedzie(): Promise<void> {
  await zapytanie<OKReply>("POST", "/api/sesje/wyloguj-wszedzie");
}

/** Zgody na zbieranie danych własnego urządzenia (każde osobno - migracja 0045). */
export async function zgody(sn: string): Promise<ConsentOut[]> {
  return (await zapytanie<ConsentsReply>("GET", `/api/urzadzenia/${encodeURIComponent(sn)}/zgody`)).zgody;
}

export async function zapiszZgode(sn: string, klucz: string, wlaczona: boolean): Promise<void> {
  await zapytanie<OKReply>("PUT", `/api/urzadzenia/${encodeURIComponent(sn)}/zgody`, { [klucz]: wlaczona });
}

/** Nazwa urządzenia (tylko właściciel); nową listę przysyła brama. */
export async function zmienNazweUrzadzenia(sn: string, nazwa: string): Promise<void> {
  const dane: DeviceNameIn = { nazwa };
  await zapytanie<OKReply>("PUT", `/api/urzadzenia/${encodeURIComponent(sn)}/nazwa`, dane);
}

/** Wcześniej wpisane pojemności naczyń pompy [L], najświeższe najpierw (WYGLAD.md #86). */
export async function pojemnosciNaczyn(sn: string): Promise<number[]> {
  return (await zapytanie<TanksReply>("GET", `/api/urzadzenia/${encodeURIComponent(sn)}/naczynia`)).naczynia;
}

/** Pojemność ustawiona przy wymianie naczynia - na górę listy. */
export async function zapamietajNaczynie(sn: string, litry: number): Promise<void> {
  const dane: TankIn = { litry };
  await zapytanie<OKReply>("POST", `/api/urzadzenia/${encodeURIComponent(sn)}/naczynia`, dane);
}
